/**
 * Tonearm solver.
 *
 * The stylus sits a fixed distance ARM_EFF from the pivot. Asking "what arm
 * angle puts the stylus at radius r on the record?" is the law of cosines on
 * the triangle pivot–spindle–stylus:
 *
 *     r² = ARM_EFF² + D_PC² − 2·ARM_EFF·D_PC·cos(theta)
 *
 * where D_PC is the pivot-to-spindle distance and theta is the angle at the
 * pivot between the spindle and the stylus.
 */

import { clamp, lerp } from '../lib/math.ts';
import { CENTRE, DIM, PIVOT, type PlanePoint } from './dims.ts';

/** Distance from the pivot to the stylus, including the headshell offset. */
export const ARM_EFF = Math.hypot(DIM.arm.len, DIM.arm.offZ);
/** Angle of the stylus within the arm's own frame (the offset angle). */
export const ARM_OFF = Math.atan2(DIM.arm.offZ, DIM.arm.len);
/** Pivot to spindle. */
export const D_PC = Math.hypot(CENTRE.x - PIVOT.x, CENTRE.z - PIVOT.z);
/** World XZ bearing from the pivot to the spindle. */
export const PHI_C = Math.atan2(CENTRE.z - PIVOT.z, CENTRE.x - PIVOT.x);

/** Smallest and largest radii the arm can physically reach. */
export const REACH_MIN = Math.abs(D_PC - ARM_EFF);
export const REACH_MAX = D_PC + ARM_EFF;

/** World XZ bearing of the pivot→stylus line that puts the stylus at radius r. */
export function armPhiForRadius(r: number): number {
  const c = clamp((ARM_EFF * ARM_EFF + D_PC * D_PC - r * r) / (2 * ARM_EFF * D_PC), -1, 1);
  return PHI_C - Math.acos(c);
}

/** Y rotation of the arm pivot group that puts the stylus at radius r. */
export const armYawForRadius = (r: number): number => ARM_OFF - armPhiForRadius(r);

/** Where the stylus lands, in world XZ, for a given pivot bearing. */
export const stylusPointForPhi = (phi: number): PlanePoint => ({
  x: PIVOT.x + Math.cos(phi) * ARM_EFF,
  z: PIVOT.z + Math.sin(phi) * ARM_EFF,
});

/** Distance from the spindle for a given pivot bearing. */
export function stylusRadiusForPhi(phi: number): number {
  const p = stylusPointForPhi(phi);
  return Math.hypot(p.x - CENTRE.x, p.z - CENTRE.z);
}

/** Inverse of {@link armYawForRadius}: the radius a given arm yaw produces. */
export const radiusForArmYaw = (yaw: number): number => stylusRadiusForPhi(ARM_OFF - yaw);

export const YAW_LEAD = armYawForRadius(DIM.leadInR);
export const YAW_END = armYawForRadius(DIM.runOutR);
/** Parked outboard of the record, on the arm rest. */
export const YAW_REST = YAW_LEAD + 0.3;

/** How far the arm tips up about its pivot when the needle is lifted, in radians. */
export const LIFT_ANGLE = 0.052;

/** How far a point `along` the arm rises when the needle is lifted. */
export const liftRise = (along: number): number => along * Math.sin(LIFT_ANGLE);

/** 0 at the lead-in groove, 1 at the run-out. */
export const radiusForProgress = (p: number): number =>
  lerp(DIM.leadInR, DIM.runOutR, clamp(p, 0, 1));

export const progressForRadius = (r: number): number =>
  clamp((DIM.leadInR - r) / (DIM.leadInR - DIM.runOutR), 0, 1);

export const armYawForProgress = (p: number): number => armYawForRadius(radiusForProgress(p));

/**
 * Progress implied by pointing the arm's bearing at a world point. Used while
 * the user drags the tonearm to seek: only the bearing matters, not how far
 * out along the arm the finger happens to be.
 */
export function progressFromArmPoint(x: number, z: number): number {
  const phi = Math.atan2(z - PIVOT.z, x - PIVOT.x);
  return progressForRadius(stylusRadiusForPhi(phi));
}

/** Where the arm rest sits — just outboard of the parked stylus. */
export function armRestPoint(inset = 1.4): PlanePoint & { phi: number } {
  const phi = ARM_OFF - YAW_REST;
  return {
    x: PIVOT.x + Math.cos(phi) * (ARM_EFF - inset),
    z: PIVOT.z + Math.sin(phi) * (ARM_EFF - inset),
    phi,
  };
}

/*
 * The arm under a hand.
 *
 * A drag is not bound to the record the way playback is: the hand can carry the
 * arm from the run-out groove all the way out to its rest, and a little past
 * either end before the stop bites.
 */

/** Arm yaw that aims the arm's own line at a world point — unclamped. */
export const armYawForPoint = (x: number, z: number): number =>
  ARM_OFF - Math.atan2(z - PIVOT.z, x - PIVOT.x);

/** 0 at the lead-in, 1 at the run-out, and 0 anywhere outboard of the record. */
export const progressForArmYaw = (yaw: number): number => progressForRadius(radiusForArmYaw(yaw));

/** How far past either stop the arm gives when pushed by hand, in radians. */
export const DRAG_GIVE = 0.05;

/** Compresses an overshoot into (0, DRAG_GIVE), with unit slope at zero. */
const give = (excess: number): number => (DRAG_GIVE * excess) / (DRAG_GIVE + excess);

/**
 * The arm's travel under a hand: the run-out groove to the rest, with a little
 * give at each end so a stop feels like a stop rather than a wall.
 */
export function clampDragYaw(yaw: number): number {
  if (yaw > YAW_REST) return YAW_REST + give(yaw - YAW_REST);
  if (yaw < YAW_END) return YAW_END - give(YAW_END - yaw);
  return yaw;
}

/** Let go beyond this yaw, the arm settles on its rest rather than the record. */
export const YAW_PARK = (YAW_LEAD + YAW_REST) / 2;

/** Whether a released arm is nearer its rest than the lead-in groove. */
export const shouldPark = (yaw: number): boolean => yaw > YAW_PARK;
